// Pure helpers behind the "Zoho details missing" modal, kept free of React so
// they can be unit tested on their own. The modal owns the UI; everything that
// decides *what* to show lives here.

// A GST is compared trimmed and upper-cased, both sides. The dump is generated
// from whatever Zoho holds, which is not normalised at source.
export const normalise_gst = (gst) => (gst === null || gst === undefined) ? '' : String(gst).trim().toUpperCase();

/**
 * Filter the Zoho contacts dump down to the rows whose GST matches.
 * Returns [] for a blank GST — a blank must never match the rows whose
 * zoho_gst is itself blank, which is a large share of the dump.
 */
export const find_gst_matches = (dump, gst) => {
	const target = normalise_gst(gst);
	if (!target) return [];
	if (!Array.isArray(dump)) return [];
	return dump.filter(row => row && normalise_gst(row.zoho_gst) === target);
};

// One of: 'single' | 'none' | 'multiple' | 'unavailable'
export const derive_match_state = (matches, dump_unavailable) => {
	if (dump_unavailable) return 'unavailable';
	if (!Array.isArray(matches) || matches.length === 0) return 'none';
	if (matches.length === 1) return 'single';
	return 'multiple';
};

// RFC 4180: quote every field, double any embedded quote. Zoho names routinely
// contain commas, so unquoted output would silently corrupt the sheet.
const csv_cell = (value) => {
	const s = (value === null || value === undefined) ? '' : String(value);
	return '"' + s.replace(/"/g, '""') + '"';
};

export const CSV_COLUMNS = ['zoho contact id', 'zoho name', 'zoho gst', 'infurnia gst', 'infurnia name'];

/**
 * Comparison sheet for the Accounts admin: one row per candidate Zoho contact,
 * with the current Infurnia org's GST and name repeated on every row so the
 * admin can see which Zoho contact should stay active.
 */
export const build_multi_match_csv = (matches, infurnia_gst, infurnia_name) => {
	const rows = [CSV_COLUMNS.map(csv_cell).join(',')];
	(Array.isArray(matches) ? matches : []).forEach(match => {
		rows.push([
			csv_cell(match && match.zoho_contact_id),
			csv_cell(match && match.zoho_name),
			csv_cell(match && match.zoho_gst),
			csv_cell(infurnia_gst),
			csv_cell(infurnia_name),
		].join(','));
	});
	return rows.join('\n');
};

/**
 * Body for billing/order/get_zoho_estimate.
 *
 * The backend requires customer_store_id whenever zoho_contact_id is sent, and
 * rejects any of the three as a non-string with INVALID_PARAMS — so both rules
 * are enforced here, at the single place the body is built.
 *
 * With no resolved contact the body is exactly {order_id}, i.e. byte-identical
 * to the pre-existing call for every org that already has a contact id.
 */
export const build_estimate_body = (order_id, store_id, resolved) => {
	const body = { order_id };
	const zoho_contact_id = resolved && resolved.zoho_contact_id;
	if (!zoho_contact_id) return body;
	body.zoho_contact_id = String(zoho_contact_id);
	body.customer_store_id = String(store_id);
	// Only sent when the user edited the GST — an unedited GST needs no writeback.
	if (resolved.gst_number) body.gst_number = String(resolved.gst_number);
	return body;
};

// general_fetch throws a stringified envelope, so error codes are read by
// substring match on err.err.message — see components/EditTdsModal/index.jsx:150.
export const is_dump_unavailable_error = (err) => {
	const msg = err && err.err && err.err.message ? String(err.err.message) : '';
	return msg.includes('ZOHO_CONTACTS_DUMP_NOT_AVAILABLE');
};
